import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { Button } from "@/components/ui/button";
import {
  breadcrumbSchema,
  faqSchema,
  jsonLd,
  absUrl,
  languageAlternates,
  localBusinessSchema,
  organizationSchema,
} from "@/lib/seo";
import { withBookingUtm } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import { logEnClick } from "@/lib/enClick";
import { PRICE, priceName } from "@/data/pricing";
import { salons } from "@/data/salons";
import { reviewsForSalon } from "@/data/reviews";
import citySalonImage from "@/assets/opt/ahlens-utsida.webp";
import AhlensWayfinder from "@/components/AhlensWayfinder";

const TITLE = "Barber Near Me in Stockholm, Walk-ins Welcome | Tailor's Barber";
const DESCRIPTION = "English-speaking barbers in Stockholm. Haircut 450 SEK, fades and beard trims. Four salons, 1 minute from T-Centralen. Book online or walk in.";

const BOOK_URL = withBookingUtm("https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678");

const services = [
  ["Men's haircut", PRICE.herrklippning],
  ["Skin fade", PRICE.fade],
  ["Taper fade", PRICE.fade],
  ["Beard trim", PRICE.skaggtrimning],
  ["Haircut and beard", PRICE.klippningSkagg],
  [`Kids haircut (${priceName("Barnklippning").match(/\((.*)\)/)?.[1] ?? "up to 10 years"})`, PRICE.barnklippning],
  [`Senior haircut (${priceName("Seniorklippning 65+").match(/\((.*)\)/)?.[1] ?? "Monday to Thursday"})`, PRICE.seniorklippning],
  ["Classic shave", PRICE.slatrakning],
] as const;

const englishServiceSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Barber services in Stockholm",
  itemListElement: services.map(([name, price], index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name,
      provider: { "@id": "https://tailorsbarber.se/#organization" },
      areaServed: { "@type": "City", name: "Stockholm" },
      offers: { "@type": "Offer", price, priceCurrency: "SEK", availability: "https://schema.org/InStock" },
    },
  })),
};

/** "/barberare-stockholm-taby" -> "taby" */
const salonSlugOf = (path: string) => path.replace("/barberare-stockholm-", "");

const locations = [
  {
    name: "Stockholm City Centre, Åhléns",
    path: "/barberare-stockholm-ahlens-city",
    address: "Klarabergsgatan 50, 3rd floor (men's department), 111 21 Stockholm",
    extra: "1 minute from T-Centralen (central subway station)",
    hours: "Mon to Fri 10:00 to 20:00 | Sat 10:00 to 19:00 | Sun 11:00 to 19:00",
    book: "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
  },
  {
    name: "Täby Centrum",
    path: "/barberare-stockholm-taby",
    address: "Biblioteksgången 1, 183 34 Täby",
    hours: "Mon to Fri 10:00 to 20:00 | Sat and Sun 10:00 to 18:00",
    book: "https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677",
  },
  {
    name: "Åkersberga, ICA Maxi",
    path: "/barberare-stockholm-akersberga",
    address: "Långhundravägen 9, 184 44 Åkersberga",
    hours: "Mon to Fri 10:00 to 19:00 | Sat and Sun 10:00 to 18:00",
    book: "https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682",
  },
  {
    name: "Arninge, ICA Maxi",
    path: "/barberare-stockholm-arninge",
    address: "Kundvägen 1D, 187 66 Täby",
    hours: "Mon to Fri 10:00 to 19:00 | Sat and Sun 10:00 to 18:00",
    book: "https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598",
  },
];


const faq = [
  { q: "How much does a haircut cost at Tailor's Barber?", a: "A men's haircut costs 450 SEK. Skin fade and taper fade also 450 SEK." },
  { q: "Do I need to book in advance?", a: "We recommend booking online, but walk-in is available subject to availability." },
  { q: "Where is the closest barber to Stockholm Central Station?", a: "Our Åhléns City location is a 1-minute walk from T-Centralen." },
  { q: "Do you offer beard trimming?", a: "Yes. Beard trim is 380 SEK. Haircut + beard combo is 680 SEK." },
  { q: "Are you open on Sundays?", a: "Yes, all locations are open on Sundays." },
  { q: "Do you cut children's hair?", a: "Yes, kids haircuts (up to age 10) for 380 SEK." },
  { q: "What's the difference between a taper fade and a skin fade?", a: "A skin fade goes all the way down to the skin, while a taper fade leaves a little length at the bottom for a softer look." },
  { q: "Can I walk in without an appointment?", a: "Yes, walk-in (drop-in) is available at all locations, subject to availability." },
];


export const Route = createFileRoute("/en")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: absUrl("/en") },
      { property: "og:type", content: "website" },
      { httpEquiv: "content-language", content: "en" },
    ],

    links: [
      { rel: "canonical", href: absUrl("/en") },
      ...languageAlternates("/", "/en"),
    ],
    scripts: [
      jsonLd(organizationSchema()),
      ...salons.map((salon) => jsonLd(localBusinessSchema(
        salon,
        reviewsForSalon(salon.slug),
        {
          name: `${salon.name}, English-speaking barbers`,
          description: `English-speaking barbers at ${salon.shortName}. Men's haircuts, fades, beard trims, walk-ins and online booking in Stockholm.`,
        },
      ))),
      jsonLd(englishServiceSchema),
      jsonLd(breadcrumbSchema([{ name: "Home", url: "/" }, { name: "English", url: "/en" }])),
      jsonLd(faqSchema(faq)),
    ],
  }),
  component: EnglishHome,
});

function EnglishHome() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Home</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">English</span>
        </nav>
        <div className="eyebrow">Barber Stockholm</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Barber Near Me in Stockholm, Walk-ins Welcome
        </h1>
        <p className="mt-5 text-lg text-foreground/80 max-w-prose leading-relaxed">
          For a trusted barber Stockholm visitors and residents can reach easily, choose one of our four English-speaking barbershops. Book online in seconds or visit us as a walk-in.
        </p>
        <aside className="mt-5 max-w-3xl border-l-2 border-brass pl-4 text-sm leading-relaxed text-foreground/80">
          <strong className="text-foreground">Quick answer:</strong> Tailor&apos;s Barber has four barbershops in Stockholm: Åhléns City by T-Centralen, Täby Centrum, Åkersberga and Arninge. Our English-speaking barbers offer men&apos;s haircuts for {PRICE.herrklippning} SEK and beard trims for {PRICE.skaggtrimning} SEK. Walk-ins are welcome, and online booking is available through BokaDirekt.
        </aside>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Book your appointment</a>
          </Button>
        </div>
        <img
          src={citySalonImage}
          alt="Barber near me in Stockholm at Tailor's Barber Åhléns City"
          width={1200}
          height={1601}
          loading="eager"
          className="mt-8 max-h-80 w-full rounded object-cover"
        />
      </section>

      <section className="container-page min-w-0 overflow-hidden py-10 [&_.wf>div:last-child>div:first-child]:min-w-0">
        <h2 className="mb-5 text-2xl md:text-3xl">Finding us</h2>
        <AhlensWayfinder lang="en" />
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Why choose Tailor&apos;s Barber?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Tailor&apos;s Barber is a premium barbershop with four locations across Stockholm, Sweden. We
          specialise in men&apos;s haircuts, skin fades, taper fades, beard trims and classic shaves,
          always with a focus on quality and style. Our barbers can discuss your preferred length,
          finish and daily routine in English before the service begins. That consultation helps us
          choose a result that suits your hair texture, face shape and maintenance preferences.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-5">What services do you offer?</h2>
        <p className="mb-5 max-w-3xl leading-relaxed text-foreground/80">
          A skin fade blends the hair down to bare skin for a crisp, high-contrast finish. A taper
          fade keeps more length and gradually shortens the hair around the temples and neckline for
          a softer result. Tell your barber how often you want to maintain the cut and bring a
          reference photo if you have a specific finish in mind.
        </p>
        <div className="card-elev overflow-hidden max-w-3xl">
          <table className="w-full text-left">
            <tbody>
              {services.map(([name, price], i) => (
                <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                   <td className="px-5 py-4 text-right font-medium whitespace-nowrap">{price} SEK</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Where is the nearest Tailor&apos;s Barber in Stockholm?</h2>
        <p className="mt-3 text-sm text-foreground/70">
          Jump straight to a salon page or book it directly on BokaDirekt:{" "}
          {locations.map((l, i) => (
            <span key={l.path}>
              {i > 0 && " · "}
              <Link
                to={internalPath(l.path)}
                className="underline font-medium"
                onClick={() =>
                  logEnClick({
                    linkType: "internal_salon",
                    salonSlug: salonSlugOf(l.path),
                    ctaLocation: "en_locations_jumplinks",
                    linkUrl: l.path,
                  })
                }
              >
                {l.name}
              </Link>
            </span>
          ))}
        </p>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {locations.map((l) => (
            <article key={l.name} className="card-elev p-6">
              <h3 className="text-xl font-medium">
                <Link
                  to={internalPath(l.path)}
                  className="hover:underline"
                  onClick={() =>
                    logEnClick({
                      linkType: "internal_salon",
                      salonSlug: salonSlugOf(l.path),
                      ctaLocation: "en_location_card",
                      linkUrl: l.path,
                    })
                  }
                >
                  {l.name}
                </Link>
              </h3>
              <p className="mt-2 text-sm text-foreground/80">{l.address}</p>
              {l.extra && <p className="mt-1 text-sm text-foreground/70">{l.extra}</p>}
              <p className="mt-2 text-sm text-foreground/70">Hours: {l.hours}</p>
               <p className="mt-2 text-sm leading-relaxed text-foreground/70">
                 {l.path.includes("ahlens")
                   ? "Take any metro line to T-Centralen and walk about one minute to Åhléns City. The salon is on the third floor."
                   : l.path.includes("taby")
                     ? "Take Roslagsbanan to Täby Centrum. The shopping centre entrance and salon are a short walk from the station."
                     : l.path.includes("akersberga")
                       ? "Take Roslagsbanan to Åkersberga, then continue by local bus or walk to ICA Maxi. Free parking is available."
                       : "Travel by bus to Arninge station. The salon is inside ICA Maxi, with free parking for visitors."}
               </p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <Button asChild size="sm">
                  <a
                    href={withBookingUtm(l.book)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cta="en_location_card"
                    aria-label={`Book at ${l.name} on BokaDirekt`}
                    onClick={() =>
                      logEnClick({
                        linkType: "bokadirekt",
                        salonSlug: salonSlugOf(l.path),
                        ctaLocation: "en_location_card",
                        linkUrl: l.book,
                      })
                    }
                  >
                    Book at {l.name}
                  </a>
                </Button>
                <Link
                  to={internalPath(l.path)}
                  className="text-sm font-medium underline"
                  onClick={() =>
                    logEnClick({
                      linkType: "internal_salon",
                      salonSlug: salonSlugOf(l.path),
                      ctaLocation: "en_location_card_details",
                      linkUrl: l.path,
                    })
                  }
                >
                  Salon details →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Can I walk in without booking?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          We combine traditional barbering with modern techniques. Our barbers specialize in men&apos;s
          grooming, from classic haircuts to precise fades and beard styling. Walk-ins are welcome at all
          locations when a barber is available. Online booking is recommended for weekends, afternoons
          and visits before an important event. Booking ahead also lets you choose a salon, service and
          available barber before you arrive.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">How much is a haircut in Stockholm?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          A men&apos;s haircut costs {PRICE.herrklippning} SEK at Tailor&apos;s Barber. Whether you are in the city centre near Sergels
          Torg, in Täby, Åkersberga or Arninge, we have a location close to you. The listed price includes
          consultation, cutting and styling. Our barbershops offer both walk-in service and online booking
          through BokaDirekt, so you can compare available times before travelling.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {locations.map((l) => (
            <li
              key={l.path}
              className="card-elev p-4 flex flex-wrap items-center justify-between gap-3"
            >
              <Link
                to={internalPath(l.path)}
                className="font-medium hover:underline"
                onClick={() =>
                  logEnClick({
                    linkType: "internal_salon",
                    salonSlug: salonSlugOf(l.path),
                    ctaLocation: "en_near_you",
                    linkUrl: l.path,
                  })
                }
              >
                {l.name}
              </Link>
              <a
                href={withBookingUtm(l.book)}
                target="_blank"
                rel="noopener noreferrer"
                data-cta="en_near_you"
                aria-label={`Book at ${l.name} on BokaDirekt`}
                className="text-sm font-medium text-brass-deep hover:underline"
                onClick={() =>
                  logEnClick({
                    linkType: "bokadirekt",
                    salonSlug: salonSlugOf(l.path),
                    ctaLocation: "en_near_you",
                    linkUrl: l.book,
                  })
                }
              >
                Book on BokaDirekt →
              </a>
            </li>
          ))}
        </ul>
      </section>



      <Faq items={faq} heading="Frequently Asked Questions" eyebrow="FAQ" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Ready to book?</h2>
          <p className="mt-3 text-foreground/80">Pick your time, location and barber online.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Book your appointment</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
